'use client';

import { useCallback } from 'react';

import {
  apiGtm,
  buildBannerPromotion,
  useGTMViewPromotionCarousel,
  useGTMViewPromotionItemRef
} from '@/api/gtm';
import { PageBlock } from '@/payload/payload-types-block';
import { UiBannerCarouselBase } from '@/ui/banner-carousel-base';
import { filterBySchedule } from '@/util/is-schedule-active';
import { toAbsoluteUrl } from '@/util/to-absolute-url';

type BannerBase = PageBlock<'bannerBase'>;

const getBannerName = (banner: BannerBase['slides'][number]) =>
  [banner.title?.firstLine, banner.title?.secondLine]
    .map((item) => item?.trim())
    .filter(Boolean)
    .join(' ');

interface FeatureBannerCarouselBaseProps {
  banners: BannerBase['slides'];
  trackingName: string;
  location?: BannerBase['location'];
  className?: string;
  onLinkClick?: (label?: string) => void;
}

export function FeatureBannerCarouselBase({
  banners,
  trackingName,
  location,
  className,
  onLinkClick
}: FeatureBannerCarouselBaseProps) {
  const activeBanners = filterBySchedule(banners);
  const isSingleSlide = activeBanners.length === 1;

  const getPromotion = useCallback(
    (index: number) => {
      const banner = activeBanners[index];
      if (!banner || (!banner.button?.link?.url?.trim() && trackingName !== 'loyalty'))
        return;

      return buildBannerPromotion({
        id: banner.id,
        name: getBannerName(banner),
        imageUrl: toAbsoluteUrl(
          typeof banner.image === 'number' ? '' : (banner.image?.url ?? '')
        ),
        linkUrl: banner.button?.link?.url,
        trackingName,
        index
      });
    },
    [activeBanners, trackingName]
  );

  const bannerRef = useGTMViewPromotionItemRef(getPromotion);
  const onCarouselInit = useGTMViewPromotionCarousel(getPromotion);

  const handleBannerClick = (index: number): boolean => {
    const promotion = getPromotion(index);
    if (!promotion) return false;

    apiGtm.selectPromotion({
      ecommerce: {
        promoClick: {
          promotions: [promotion]
        }
      }
    });
    return true;
  };

  if (!activeBanners.length) return null;

  return (
    <UiBannerCarouselBase
      banners={activeBanners}
      location={location}
      className={className}
      onLinkClick={onLinkClick}
      bannerRef={isSingleSlide ? bannerRef : undefined}
      onCarouselInit={isSingleSlide ? undefined : onCarouselInit}
      onBannerClick={handleBannerClick}
    />
  );
}
